import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  presentQuantityLadder,
  savingLabel,
  type QuantityLadderDisplayRow,
} from "@shared-core/domain/quantityLadder";
import type { CustomerQuantityLadderRow } from "@shared-core/data/customer-api-types";
import { Badge } from "../../app/components/primitives/badge";
import {
  RadioGroup,
  RadioGroupTileItem,
} from "../../app/components/primitives/radio-group";

function toDisplayRow(
  row: CustomerQuantityLadderRow,
  selectedQuantity: number | null,
): QuantityLadderDisplayRow {
  return {
    quantity: row.quantity,
    available: row.available,
    totalPence: row.total_pence ?? null,
    unitPricePence: row.unit_price_pence ?? null,
    isSelected: row.quantity === selectedQuantity,
  };
}

/** The ladder's rows in quantity order with each one's saving and best-value marker. A quantity with no priced row stays selectable, as "price on request". */
export function presentLadderRows(
  rows: CustomerQuantityLadderRow[],
  quantities: number[],
  selectedQuantity: number | null,
) {
  const sourceQuantities =
    rows.length > 0 ? rows.map((r) => r.quantity) : quantities;
  const byQuantity = new Map(rows.map((r) => [r.quantity, r]));
  return presentQuantityLadder(
    sourceQuantities
      .slice()
      .sort((a, b) => a - b)
      .map((qty) => {
        const row = byQuantity.get(qty);
        return row
          ? toDisplayRow(row, selectedQuantity)
          : {
              quantity: qty,
              available: false,
              totalPence: null,
              unitPricePence: null,
              isSelected: qty === selectedQuantity,
            };
      }),
  );
}

// The quantity choice as a visible price ladder rather than a dropdown
// (Brad's portal feedback, 2026-10-02; the catalogue item page has used the
// compact catalogue-item/CompactQuantityLadder since 2026-10-12, so this is now the
// quote page's): every standard quantity shown at
// once with its own total, unit price and saving versus the smallest
// quantity, so a bigger run's discount is visible before it's picked. Each
// row is a radio (one click to select, keyboard-navigable, aria-checked)
// -- same semantics as OptionTileGroup, just laid out as a list instead of
// inline tiles because a price table reads better top-to-bottom. Shared by
// the catalogue item page and the quote page.
export function QuantityLadder({
  rows,
  quantities,
  selectedQuantity,
  onSelect,
  disabled = false,
  deliveryNotes,
}: {
  rows: CustomerQuantityLadderRow[];
  /** Fallback when `rows` hasn't arrived yet (still loading, or pricing
   *  failed entirely) -- the quantities stay selectable, just without a
   *  price column, rather than disappearing. */
  quantities: number[];
  selectedQuantity: number | null;
  onSelect: (quantity: number) => void;
  disabled?: boolean;
  /** Each quantity's delivery in words ("delivery £12.00"), where the quote prices delivery per quantity. */
  deliveryNotes?: ReadonlyMap<number, string>;
}) {
  const presented = presentLadderRows(rows, quantities, selectedQuantity);

  return (
    <RadioGroup
      value={selectedQuantity !== null ? String(selectedQuantity) : ""}
      onValueChange={(value) => onSelect(Number(value))}
      aria-label="Quantity"
      disabled={disabled}
      // `grid-cols-[minmax(0,1fr)]`: the group is a grid, and its implicit
      // single column is `auto`, which grows to the widest tile's min-content
      // and pushes the tiles out past the buy-box card. Pin the column to the
      // container and let the tiles' own content wrap instead.
      className="grid-cols-[minmax(0,1fr)] gap-2"
    >
      {presented.map((row) => (
        <RadioGroupTileItem
          key={row.quantity}
          value={String(row.quantity)}
          disabled={!row.available}
          className="flex w-full min-w-0 items-center justify-between gap-x-4 gap-y-1 rounded-lg border px-4 py-2.5 text-left font-normal"
        >
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold tabular-nums">
              {row.quantity.toLocaleString("en-GB")}
            </span>
            {row.isBestValue && <Badge variant="success">Best value</Badge>}
          </span>
          {row.available ? (
            <span className="flex shrink-0 items-center gap-3 text-right">
              {savingLabel(row.savingPct) && (
                <Badge variant="outline">{savingLabel(row.savingPct)}</Badge>
              )}
              <span className="flex flex-col items-end">
                <span className="font-semibold tabular-nums">
                  {formatQuotedPrice(row.totalPence ?? 0)}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatQuotedPrice(row.unitPricePence ?? 0)}/unit
                </span>
                {deliveryNotes?.get(row.quantity) && (
                  <span className="text-xs text-muted-foreground">
                    {deliveryNotes.get(row.quantity)}
                  </span>
                )}
              </span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              Price on request
            </span>
          )}
        </RadioGroupTileItem>
      ))}
    </RadioGroup>
  );
}
